/* La skin: chi e' il cliente che sta guardando questo sito.
 *
 * Si ricava dal DOMINIO, una volta sola all'avvio, e vale per tutta la
 * sessione. Da qui vengono il marchio, i colori, il carattere, le sezioni
 * di gioco accese e l'albero dei moduli composto dal backoffice.
 *
 * Campi del tenant che arrivano da by-domain (letti dallo schema del
 * servizio, non inventati):
 *   id, name, domain, logoUrl, currency, colors, fonts, sections, fuso,
 *   formatoQuote, registrazione, moduli,
 *   lingue (codice e nome di ognuna, l'italiano in testa), linguaPrima,
 *   status: ENABLED | DISABLED | PENDING
 * Le parole proprie della skin NON arrivano da qui: il sito le riceve gia'
 * dentro il dizionario (GET /api/tenants/dizionario/<lingua>?domain=...).
 * In anteprima la bozza puo' portare `parole` (lingua → frase → parola) e
 * `lingue`: i18n.js le applica sopra il dizionario senza salvarle.
 *
 * ATTENZIONE alla forma della risposta: quando la skin non esiste, il
 * servizio risponde con codice HTTP 200 e il 404 scritto dentro il corpo
 *   { "message": "Resource not found!", "status": 404, "tenant": null }
 * quindi non basta guardare il codice della risposta: va guardato il corpo.
 *
 * L'ANTEPRIMA. Aperto con ?anteprima=1 (dentro la cornice del backoffice)
 * il sito non usa il deposito di mezz'ora, si annuncia alla finestra che lo
 * contiene e accetta da lei la BOZZA della skin con un messaggio fra
 * finestre: la applica ai colori e ai moduli senza salvarla da nessuna
 * parte. Quello che si vede in anteprima e' il sito vero con la bozza
 * sopra, non una copia.
 */

import catalogo from './moduli/catalogo.json';

const CHIAVE = 'lvc.skin';
const DURATA = 30 * 60 * 1000;   /* mezz'ora */
const ATTESA_MASSIMA = 1500;     /* oltre, si parte senza: meglio un sito
                                    senza marchio che un sito che non parte */

let skin = null;
const ascoltatori = new Set();

/* IL SITO SPENTO (3 novembre 2026). Una skin eliminata o disattivata dal
   backoffice lasciava il sito in linea col guscio di serie, senza marchio:
   chi apriva l'indirizzo vedeva un sito che sembrava vivo. Ora, quando il
   servizio risponde DAVVERO che quel dominio non ha una skin accesa (non
   quando non risponde), il sito si spegne e mostra «non disponibile».
   Non vale in anteprima, su localhost e su un indirizzo IP: li' si prova
   il sito senza un dominio di skin, ed e' legittimo. */
let spento = false;

/** Il servizio ha detto che questo dominio non ha una skin accesa. */
export function sitoSpento() {
  return spento;
}

function dominioDiSkin() {
  if (typeof window === 'undefined' || inAnteprima()) return false;
  const h = (window.location.hostname || '').toLowerCase();
  if (!h || h === 'localhost' || h.endsWith('.localhost')) return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h) || h.includes(':')) return false;
  return true;
}

function indirizzoApi() {
  const env = (typeof window !== 'undefined' && window.env) || {};
  return env.REACT_APP_API_URL || '';
}

/** Il tenant, comunque il servizio lo incarti. */
function estrai(corpo) {
  if (!corpo) return null;
  if (corpo.tenant) return corpo.tenant;
  if (corpo.status === 404) return null;
  if (corpo.id || corpo.domain) return corpo;
  return null;
}

/** Siamo dentro la cornice dell'anteprima del backoffice? */
export function inAnteprima() {
  try {
    return new URLSearchParams(window.location.search).has('anteprima');
  } catch (e) {
    return false;
  }
}

function daDeposito() {
  if (inAnteprima()) return null;
  try {
    const grezzo = window.sessionStorage.getItem(CHIAVE);
    if (!grezzo) return null;
    const { quando, dato } = JSON.parse(grezzo);
    if (Date.now() - quando > DURATA) return null;
    return dato;
  } catch (e) {
    return null;
  }
}

function inDeposito(dato) {
  if (inAnteprima()) return;
  try {
    const grezzo = JSON.stringify({ quando: Date.now(), dato });
    window.sessionStorage.setItem(CHIAVE, grezzo);
    window.localStorage.setItem(CHIAVE, grezzo);
  } catch (e) { /* navigazione privata: pazienza, si richiede */ }
}

/* LA COPIA LUNGA. Il deposito di mezz'ora sta nella sessione: un'altra
   scheda o una visita il giorno dopo ripartivano da zero e aspettavano la
   skin (fino a un secondo e mezzo) prima di disegnare qualunque cosa. La
   copia nel browser vale un giorno: con quella il sito parte SUBITO col
   marchio dell'ultima volta, e intanto la skin si richiede di nascosto e,
   se e' cambiata, si ridisegna. */
const DURATA_LUNGA = 24 * 60 * 60 * 1000;
function daCopiaLunga() {
  if (inAnteprima()) return null;
  try {
    const grezzo = window.localStorage.getItem(CHIAVE);
    if (!grezzo) return null;
    const { quando, dato } = JSON.parse(grezzo);
    if (Date.now() - quando > DURATA_LUNGA) return null;
    return dato;
  } catch (e) {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * I colori e il carattere della skin -> le variabili CSS.
 *
 * Il backoffice salva cinque colori con i nomi del CATALOGO (catalogo.json,
 * «aspetto.colori»): primary -> --lvc-skin, secondary -> --lvc-nav-1,
 * background -> --lvc-bg, surface -> --lvc-surface-1, text -> --lvc-text-1.
 * La corrispondenza nome -> variabile sta nel catalogo, cosi' il backoffice
 * mostra gli stessi nomi e le stesse spiegazioni.
 * Si scrive SOLO quello che e' un colore: nel modulo vecchio Sfondo e
 * Superfici nascevano come «var(--aet-fondo)», una variabile del backoffice
 * che qui non esiste. Un valore cosi' si ignora e resta il colore del
 * sistema: niente pagina nera per un campo mai toccato.
 * ------------------------------------------------------------------ */
const VARIABILI_COLORE = Object.fromEntries(
  Object.entries((catalogo.aspetto && catalogo.aspetto.colori) || {})
      .map(([nome, c]) => [nome, c.variabile]));

export function eColore(v) {
  return typeof v === 'string'
    && /^(#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/]+\))$/i.test(v.trim());
}

function eCarattere(v) {
  return typeof v === 'string' && v.trim().length > 0 && v.length < 200
    && /^[\w\s,'"-]+$/.test(v) && !/var\(/.test(v);
}

function applicaAspetto(t) {
  if (typeof document === 'undefined') return;
  const radice = document.documentElement.style;
  Object.keys(VARIABILI_COLORE).forEach((nome) => {
    const v = t && t.colors && t.colors[nome];
    if (eColore(v)) radice.setProperty(VARIABILI_COLORE[nome], v.trim());
    else radice.removeProperty(VARIABILI_COLORE[nome]);
  });
  const corpo = t && t.fonts && t.fonts.body;
  if (eCarattere(corpo)) radice.setProperty('--lvc-font', corpo.trim());
  else radice.removeProperty('--lvc-font');
  /* Lo stesso carattere, ma SOLO se la skin ne ha scritto uno: --lvc-font ha
     un valore di serie in tokens.css, e chi vuole un carattere suo di serie
     (il guscio Nova, Plus Jakarta Sans) non saprebbe se la skin ha parlato. */
  if (eCarattere(corpo)) radice.setProperty('--lvc-carattere-skin', corpo.trim());
  else radice.removeProperty('--lvc-carattere-skin');
}

function applica(t) {
  skin = t;
  if (typeof window !== 'undefined') window.__skin = t;
  applicaAspetto(t);
  ascoltatori.forEach((f) => { try { f(t); } catch (e) { /* un ascoltatore rotto non ferma gli altri */ } });
}

/**
 * Chiede la skin del dominio da cui si sta guardando il sito.
 * Non lancia mai: se qualcosa va storto la skin resta nulla e il sito
 * funziona lo stesso, senza marchio.
 */
export async function caricaSkin() {
  const base = indirizzoApi();
  /* Con una copia in mano (quella della sessione o quella di ieri) il sito
     parte subito con quella, ma la skin si richiede SEMPRE di nascosto: una
     modifica pubblicata dal backoffice (l'ordine dei blocchi della Home, un
     colore) si vede al primo caricamento della pagina, non dopo mezz'ora. */
  const copia = daDeposito() || (base ? daCopiaLunga() : null);
  if (copia) {
    applica(copia);
    avviaAnteprima();
    if (base) {
      chiediSkin(base, 8000).then((t) => {
        if (t === undefined) return;               /* non ha risposto: resta la copia */
        if (JSON.stringify(t) !== JSON.stringify(copia)) applica(t);
      });
    }
    return copia;
  }

  if (!base) { applica(null); avviaAnteprima(); return null; }

  /* LA PRIMA VISITA (nessuna copia: un telefono nuovo, un browser pulito).
     Si aspetta la skin al massimo ATTESA_MASSIMA, ma la richiesta NON si
     interrompe li': su una rete lenta (il telefono in 4G) il servizio puo'
     rispondere dopo. Prima la richiesta veniva tagliata a un secondo e mezzo
     e il sito restava per tutta la visita SENZA skin, cioe' col guscio «di
     sempre» e i colori di serie; e non salvando niente, la volta dopo da
     capo. Ora, se arriva tardi, si applica appena arriva (e si salva). */
  const richiesta = chiediSkin(base, 8000);
  const t = await Promise.race([richiesta, aspetta(ATTESA_MASSIMA)]);
  if (t === undefined) {
    applica(null);
    avviaAnteprima();
    richiesta.then((tardi) => {
      if (!tardi) { if (spento) applica(null); return; }
      const bozzaSopra = skin !== caricata;   /* in anteprima la bozza resta sopra */
      caricata = tardi;
      if (!bozzaSopra) applica(tardi);
    });
    return null;
  }
  applica(t);
  avviaAnteprima();
  return t;
}

function aspetta(ms) {
  return new Promise((fatto) => setTimeout(() => fatto(undefined), ms));
}

/* La skin dal servizio: il tenant, null se non c'e' o e' spenta, undefined
   se il servizio non ha risposto in tempo. */
async function chiediSkin(base, attesa) {
  const dominio = window.location.hostname;
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), attesa);

  try {
    const risposta = await fetch(
      base + '/api/tenants/by-domain?domain=' + encodeURIComponent(dominio),
      { signal: stop.signal });
    const t = estrai(await risposta.json());
    if (!t || t.status === 'DISABLED') {
      dimentica();
      spento = dominioDiSkin();
      return null;
    }
    spento = false;
    inDeposito(t);
    return t;
  } catch (e) {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

function dimentica() {
  try {
    window.sessionStorage.removeItem(CHIAVE);
    window.localStorage.removeItem(CHIAVE);
  } catch (e) { /* niente da togliere */ }
}

/** La skin gia' caricata. Sincrona: si usa nei componenti. */
export function skinCorrente() {
  return skin;
}

/** Il simbolo di valuta della skin. Finche' non c'e', l'euro. */
export function valuta() {
  return (skin && skin.currency) || 'EUR';
}

/**
 * Chi vuole sapere quando la skin cambia (in anteprima cambia a ogni
 * ritocco nel backoffice). Restituisce la funzione per smettere.
 */
export function ascoltaSkin(f) {
  ascoltatori.add(f);
  return () => ascoltatori.delete(f);
}

/**
 * Applica una bozza della skin sopra quella caricata, senza salvarla:
 * e' quello che fa l'anteprima, e quello che fa il banco di prova per
 * disegnare una skin finta. Passare null toglie la bozza.
 */
let caricata = null;   /* la skin come e' arrivata dal servizio, senza bozza sopra */
export function applicaBozza(bozza) {
  if (!bozza) { applica(caricata); return; }
  applica(Object.assign({}, caricata || {}, bozza));
}

/* L'anteprima: il sito dentro la cornice del backoffice.
   Si annuncia («sono pronto, con questo catalogo») e da li' in poi accetta
   la bozza con un messaggio {tipo: 'lvc-anteprima', skin: {...}}. Non si
   controlla da dove arriva il messaggio: la bozza cambia solo quello che
   vede chi ha aperto la cornice, e non viene scritta da nessuna parte. */
let anteprimaAvviata = false;
function avviaAnteprima() {
  caricata = skin;
  if (anteprimaAvviata || typeof window === 'undefined' || !inAnteprima()) return;
  anteprimaAvviata = true;
  window.addEventListener('message', (e) => {
    const m = e && e.data;
    if (!m || m.tipo !== 'lvc-anteprima') return;
    applicaBozza(m.skin || null);
    if (e.source && e.source.postMessage) {
      try { e.source.postMessage({tipo: 'lvc-anteprima-applicata'}, '*'); } catch (err) { /* niente */ }
    }
  });
  if (window.parent && window.parent !== window) {
    try {
      window.parent.postMessage({tipo: 'lvc-anteprima-pronta', catalogo: catalogo.versione}, '*');
    } catch (e) { /* nessuna cornice: non e' un'anteprima vera */ }
  }
}
